/**
 * EventViewer — Recruiter 1 / Recruiter 2 pickers.
 *
 * Mounted inside EventViewer for events on the Group Recruiting Calendar.
 * Renders nothing for events on other calendars (the GET endpoint returns
 * `is_recruiting_calendar: false` and we short-circuit).
 *
 * Edit-gate behavior is server-driven via `viewer.canEdit`:
 *   true  → two editable TypeAheads, save-on-select
 *   false → read-only display of the saved recruiter names (or "—" placeholders)
 *
 * Recruiter options come from AdminApi.getAllUplineUsers, which returns all
 * active leaders/managers and agency-builder-access users. Loaded once per
 * mount and cached in component state.
 */

import React from 'react'
import moment from 'moment'
import { MDBSpinner } from 'mdb-react-ui-kit'
import { toast } from 'react-toastify'
import { UITypeAhead } from './../../../../components/forms/form-fields'
import RecruitingMeetingService from './../../../../shared/services/RecruitingMeeting.service'

import './EventRecruiterPickers.scss'

class EventRecruiterPickers extends React.Component {
  state = {
    loading: true,
    error: null,
    // Server-supplied
    isRecruitingCalendar: false,
    viewerCanEdit: false,
    viewerCanSeeHistory: false,
    recruiterId1: null,
    recruiterId2: null,
    recruiter1Name: null,
    recruiter2Name: null,
    // Local
    // `leaderOptions` is the full pre-loaded leader pool from getAllUplineUsers.
    // `displayedOptions1` / `displayedOptions2` are the per-slot, search-filtered
    // subsets passed as `options` to each UITypeAhead. UITypeAhead's internal
    // filter is broken when no `onSearchChange` is supplied (it short-circuits
    // before filtering), so we drive the search externally — one filtered list
    // per slot so typing in one input doesn't shrink the other.
    leaderOptions: [],
    displayedOptions1: [],
    displayedOptions2: [],
    saving: false,
    historyOpen: false,
    historyLoading: false,
    historyLoaded: false,
    historyStale: false,
    historyError: null,
    history: [],
  }

  componentDidMount() {
    this._mounted = true
    this._load()
  }

  componentWillUnmount() {
    this._mounted = false
  }

  componentDidUpdate(prevProps) {
    if (prevProps.eventSourceId !== this.props.eventSourceId) this._load()
  }

  _load = async () => {
    const { eventSourceId } = this.props
    if (!eventSourceId) return
    this.setState({ loading: true, error: null })
    try {
      const data = await RecruitingMeetingService.fetchEventRecruiters(eventSourceId)
      if (!this._mounted) return

      // Short-circuit for events on other calendars — render nothing.
      if (!data || !data.is_recruiting_calendar) {
        this.setState({
          loading: false,
          isRecruitingCalendar: false,
          viewerCanEdit: false,
          viewerCanSeeHistory: false,
        })
        return
      }

      // Load recruiter pool only when we know we're going to render pickers.
      // Scoped to system_settings.recruiting---group-recruiting-permitted —
      // recruiters MUST come from the permitted list, not all leaders.
      let leaderOptions = []
      if (data.viewer && data.viewer.canEdit) {
        try {
          const resp = await RecruitingMeetingService.fetchPermittedRecruiters()
          const rows = (resp && resp.data) || []
          leaderOptions = rows.map(row => ({
            value: `${row.id}`,
            text: row.u_name,
          }))
        } catch (ex) {
          // Don't fail the whole component — read-only path is still useful.
          leaderOptions = []
        }
      }

      this.setState({
        loading: false,
        isRecruitingCalendar: true,
        viewerCanEdit: !!(data.viewer && data.viewer.canEdit),
        viewerCanSeeHistory: !!(data.viewer && (data.viewer.isBst || data.viewer.isPermitted)),
        recruiterId1: data.recruiter_id_1,
        recruiterId2: data.recruiter_id_2,
        recruiter1Name: data.recruiter_1_name,
        recruiter2Name: data.recruiter_2_name,
        leaderOptions,
        // Seed both slot lists with the full pool so the dropdown can populate
        // immediately once the user types 3+ characters.
        displayedOptions1: leaderOptions,
        displayedOptions2: leaderOptions,
        historyOpen: false,
        historyLoaded: false,
        historyStale: false,
        historyError: null,
        history: [],
      })
    } catch (ex) {
      if (!this._mounted) return
      this.setState({
        loading: false,
        error: 'Failed to load recruiters.',
      })
    }
  }

  // Look up a leader's name from the already-loaded pool by id. Used to
  // resolve the display name without a server round-trip after save.
  _findName = id => {
    if (id === null || id === undefined) return null
    const opt = this.state.leaderOptions.find(o => `${o.value}` === `${id}`)
    return opt ? opt.text : null
  }

  _save = async (nextR1, nextR2) => {
    const { eventSourceId } = this.props
    const prev = {
      recruiterId1: this.state.recruiterId1,
      recruiterId2: this.state.recruiterId2,
      recruiter1Name: this.state.recruiter1Name,
      recruiter2Name: this.state.recruiter2Name,
      displayedOptions1: this.state.displayedOptions1,
      displayedOptions2: this.state.displayedOptions2,
    }
    // Optimistic flip — resolve names locally from the leader pool so the
    // UI updates without waiting for the server, and reset both filtered
    // lists back to the full pool so the next keystroke starts fresh.
    this.setState({
      recruiterId1: nextR1,
      recruiterId2: nextR2,
      recruiter1Name: this._findName(nextR1),
      recruiter2Name: this._findName(nextR2),
      displayedOptions1: this.state.leaderOptions,
      displayedOptions2: this.state.leaderOptions,
      saving: true,
    })
    try {
      const resp = await RecruitingMeetingService.setEventRecruiters(eventSourceId, {
        recruiter_id_1: nextR1,
        recruiter_id_2: nextR2,
      })
      if (!this._mounted) return
      // No re-fetch — the optimistic update already reflects the saved
      // state; re-loading would force a spinner flash and is unnecessary
      // because the server isn't transforming the values.
      const entries = (resp && resp.history_entries) || []
      this.setState(prev => {
        if (!entries.length) return { saving: false }
        return prev.historyLoaded
          ? { saving: false, history: [...entries, ...prev.history] }
          : { saving: false, historyStale: true }
      })
    } catch (ex) {
      if (!this._mounted) return
      // Roll back to pre-save state.
      this.setState({ ...prev, saving: false })
      const status = ex && ex.response && ex.response.status
      toast.error(
        status === 403
          ? 'You are not authorized to change recruiters for this event.'
          : 'Failed to save recruiter selection. Please try again.',
        { position: toast.POSITION.TOP_RIGHT }
      )
    }
  }

  // UITypeAhead calls onSearchChange on every keystroke. We filter the pool
  // client-side (substring match on the leader name) and push the result back
  // through `options` for the matching slot.
  _onSearchChange = slotIndex => query => {
    const q = String(query || '')
      .toLowerCase()
      .trim()
    const filtered = q
      ? this.state.leaderOptions.filter(o => (o.text || '').toLowerCase().includes(q))
      : this.state.leaderOptions
    this.setState(slotIndex === 1 ? { displayedOptions1: filtered } : { displayedOptions2: filtered })
  }

  _onSelect1 = selected => {
    const value = selected && (selected.value || selected)
    const next = value === '' || value === null || value === undefined ? null : parseInt(value, 10)
    this._save(next, this.state.recruiterId2)
  }

  _onSelect2 = selected => {
    const value = selected && (selected.value || selected)
    const next = value === '' || value === null || value === undefined ? null : parseInt(value, 10)
    this._save(this.state.recruiterId1, next)
  }

  _renderTypeAhead = (slotKey, slotIndex) => {
    const { saving } = this.state
    const selectedId = slotIndex === 1 ? this.state.recruiterId1 : this.state.recruiterId2
    const otherId = slotIndex === 1 ? this.state.recruiterId2 : this.state.recruiterId1
    const sourceList = slotIndex === 1 ? this.state.displayedOptions1 : this.state.displayedOptions2
    const options = sourceList
      // Hide the user already selected in the other slot.
      .filter(o => `${o.value}` !== `${otherId}`)
      .map(o => ({
        ...o,
        checked: selectedId !== null && `${o.value}` === `${selectedId}`,
      }))
    const onSelect = slotIndex === 1 ? this._onSelect1 : this._onSelect2
    const onSearchChange = this._onSearchChange(slotIndex)
    return (
      <div className='erp-slot' key={slotKey}>
        <label className='erp-label'>Recruiter {slotIndex}</label>
        <UITypeAhead
          options={options}
          placeholder='Search leaders…'
          onSelect={onSelect}
          onSearchChange={onSearchChange}
          disabled={saving}
        />
      </div>
    )
  }

  _loadHistory = async () => {
    const { eventSourceId } = this.props
    if (!eventSourceId) return
    this.setState({ historyLoading: true, historyError: null })
    try {
      const resp = await RecruitingMeetingService.fetchEventRecruiterHistory(eventSourceId)
      if (!this._mounted) return
      this.setState({
        history: (resp && resp.data) || [],
        historyLoaded: true,
        historyStale: false,
        historyLoading: false,
      })
    } catch (ex) {
      if (!this._mounted) return
      this.setState({
        historyLoading: false,
        historyError: 'Failed to load assignment history.',
      })
    }
  }

  _toggleHistory = () => {
    const { historyOpen, historyLoaded, historyStale, historyLoading } = this.state
    const nextOpen = !historyOpen
    this.setState({ historyOpen: nextOpen })
    if (nextOpen && !historyLoading && (!historyLoaded || historyStale)) this._loadHistory()
  }

  _renderHistoryEntry = entry => {
    const from = entry.previous_user_name || 'None'
    const to = entry.new_user_name || 'None'
    const actor = entry.actor_user_name || 'Unknown user'
    const at = entry.created_at ? moment(entry.created_at).format('MMM D, YYYY h:mm A') : ''
    return (
      <li className='erp-history__entry' key={entry.id}>
        <div className='erp-history__change'>
          <span className='erp-history__slot'>Recruiter {entry.slot}:</span>{' '}
          <span className='erp-history__from'>{from}</span>
          <span className='erp-history__arrow'> → </span>
          <span className='erp-history__to'>{to}</span>
        </div>
        <div className='erp-history__meta'>
          by {actor}
          {at ? ` · ${at}` : ''}
        </div>
      </li>
    )
  }

  _renderHistory = () => {
    const { historyOpen, historyLoading, historyError, history } = this.state
    return (
      <div className='erp-history'>
        <button type='button' className='erp-history__toggle' onClick={this._toggleHistory} aria-expanded={historyOpen}>
          <span className='erp-history__caret'>{historyOpen ? '▾' : '▸'}</span>
          Assignment history
        </button>
        {historyOpen && (
          <div className='erp-history__body'>
            {historyLoading && (
              <div className='erp-history__status'>
                <MDBSpinner small />
                <span>Loading assignment history…</span>
              </div>
            )}
            {!historyLoading && historyError && (
              <div className='erp-history__status erp-history__status--error'>{historyError}</div>
            )}
            {!historyLoading && !historyError && !history.length && (
              <div className='erp-history__status'>No assignment changes recorded</div>
            )}
            {!historyLoading && !historyError && !!history.length && (
              <ul className='erp-history__list'>{history.map(this._renderHistoryEntry)}</ul>
            )}
          </div>
        )}
      </div>
    )
  }

  render() {
    const { loading, isRecruitingCalendar, viewerCanEdit, viewerCanSeeHistory, recruiter1Name, recruiter2Name, error } =
      this.state

    if (loading) {
      return (
        <div className='erp-container erp-container--loading'>
          <MDBSpinner small />
          <span>Loading recruiters…</span>
        </div>
      )
    }

    if (!isRecruitingCalendar) return null

    if (error) {
      return <div className='erp-container erp-container--error'>{error}</div>
    }

    return (
      <div className='erp-container'>
        <strong className='erp-heading'>Recruiters</strong>
        {viewerCanEdit ? (
          <div className='erp-pickers'>
            {this._renderTypeAhead('r1', 1)}
            {this._renderTypeAhead('r2', 2)}
          </div>
        ) : (
          <div className='erp-readonly'>
            <div>
              <span className='erp-label'>Recruiter 1:</span> <span>{recruiter1Name || '—'}</span>
            </div>
            <div>
              <span className='erp-label'>Recruiter 2:</span> <span>{recruiter2Name || '—'}</span>
            </div>
          </div>
        )}
        {viewerCanSeeHistory && this._renderHistory()}
      </div>
    )
  }
}

export default EventRecruiterPickers
